import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx"; // Import xlsx
import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import LoadingPanel from "../common/LoadingPanel";
import { Modal, Button } from "react-bootstrap"; // Import modal components
import { FaFileExcel, FaList } from "react-icons/fa"; // Import FaList from react-icons

import { getGroups } from "../../services/RetreatServices"; // Ensure this function is defined correctly
import { useAuth } from "../../auth/AuthContext";
import UtilMethods from "../../util/UtilMethods";
import { STATUS_OPTIONS } from "../common/Constants";

const GroupReport = () => {
  const { user } = useAuth();
  const eventsPath = process.env.REACT_APP_EVENTS_SOURCE || 'source/events.json';
  const events = require(`../../${eventsPath}`);
  const [groups, setGroups] = useState([]); // Initialize as an empty array
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    email: "",
    phone: "",
    retreatID: "",
    registrationID: "",
    status: "",
    refundAmount: "",
    name: "",
  });
  const [retreatOptions, setRetreatOptions] = useState(events);
  const [selectedRetreat, setSelectedRetreat] = useState({ name: "" }); // Initialize as an object
  const [debouncedSearch, setDebouncedSearch] = useState(selectedRetreat.name);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    const activeRetreat = events.find((event) =>
      UtilMethods.canRegister(
        event.reg_date,
        event.reg_end_date ? event.reg_end_date : event.start_date
      )
    );
    if (activeRetreat) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        retreatID: activeRetreat.id.toString(),
      }));
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSelectedRetreat({ name: debouncedSearch });
      setRetreatOptions(
        events.filter((event) =>
          event.name.toLowerCase().includes(debouncedSearch.toLowerCase())
        )
      );
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(timeoutId); // Clear timeout on component unmount or before next effect
    };
  }, [debouncedSearch]);

  const fetchGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(" $$$$$$$$$ fetchGroups $$$$$$$$$$$ filters:", filters);
      const data = await getGroups(filters);

      let groupWithParticipants = data || []; // Default to empty array if undefined

      if (!Array.isArray(groupWithParticipants)) {
        groupWithParticipants = groupWithParticipants
          ? [groupWithParticipants]
          : [];
      }

      if (groupWithParticipants.length > 0) {
        setGroups(groupWithParticipants);
      } else {
        setGroups([]);
        setError("No groups found for the specified filters.");
        setShowModal(true);
      }
    } catch (err) {
      console.error("fetchGroups Error:", err);
      if (axios.isAxiosError(err)) {
        setError(
          err.response
            ? err.response.data.message || err.message
            : "Network error, please try again."
        );
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = (template = "default") => {
    if (groups.length === 0) {
      setError(
        "No Registrations available to download. Please enter filters, List and download."
      );
      setShowModal(true);
      return;
    }

    let worksheetData;
    if (template === "Full Data") {
      worksheetData = groups.flatMap((group) => {
        if (Array.isArray(group.groupMembers)) {
          return group.groupMembers.map((member) => ({
            "Registration ID": group.registrationID,
            "Participant ID":
              group.registrationID +
              "(" +
              member.memberID +
              "/" +
              group.numberOfParticipants +
              ")",
            "Participant Name": member.firstName + " " + member.lastName,
            "Participant Email": member.email,
            "Participant Phone": member.phone,
            Age: member.age,
            Comments: member.comments,
            Gender: member.gender,
            Relation: member.relation,
            Deleted: member.deleted,
            "Registration Name": group.firstName + " " + group.lastName,
            "Registration Email": group.email,
            "Registration Phone": group.phone,
            Count: group.numberOfParticipants,
            Address: group.address,
            City: group.city,
            zip: group.zip,
            State: group.state,
            Country: group.country,
            "Emergency Contact PersonName": group.emergencyContactPersonName,
            "Emergency Contact Number": group.emergencyContactNumber,
            "Special Request": group.specialRequest,
            rooms: group.rooms,
            "Share Room": group.shareRoom,
            "Room Numbers": group.roomNumber,
            Amount: group.amount,
            "Payment Method": group.paymentMethod,
            "Paid Amount": group.paidAmount,
            "Payment Info": group.paymentInfo,
            "Retreat ID": group.retreatID,
            Status: group.status,
            "Created By": group.userID,
            "Created On": UtilMethods.formatDateTime(group.createdAt),
            "Updated By": group.updatedBy,
            "Updated At": UtilMethods.formatDateTime(group.updatedAt),
            "Waiver File": member.waiverFilePath,
          }));
        }
        return [];
      });
    } else if (template === "Participant") {
      worksheetData = groups.flatMap((group) => {
        if (Array.isArray(group.groupMembers)) {
          return group.groupMembers
            .filter((member) => !member.deleted)
            .map((member) => ({
              "Registration ID": group.registrationID,
              "Participant ID":
                group.registrationID +
                "(" +
                member.memberID +
                "/" +
                group.numberOfParticipants +
                ")",
              "Participant Name": member.firstName + " " + member.lastName,
              "Participant Email": member.email,
              "Participant Phone": member.phone,
              "Registration Email": group.email,
              "Registration Phone": group.phone,
              Gender: member.gender,
              Age: member.age,
              Relation: member.relation,
              Address: group.address,
              City: group.city,
              zip: group.zip,
              State: group.state,
              Country: group.country,
              "Emergency Contact PersonName": group.emergencyContactPersonName,
              "Emergency Contact Number": group.emergencyContactNumber,
              "Retreat ID": group.retreatID,
              Status: group.status,
            }));
        }
        return [];
      });
    } else if (template === "Summary") {
      worksheetData = groups.map((group) => ({
        "Registration ID": group.registrationID,
        Name: group.firstName + " " + group.lastName,
        "Registration Email": group.email,
        "Registration Phone": group.phone,
        Count: group.numberOfParticipants,
        "Retreat ID": group.retreatID,
        Status: group.status,
        Amount: group.amount,
        "Paid Amount": group.paidAmount,
        "Payment Method": group.paymentMethod,
        "Payment Info": group.paymentInfo,
        "refund Amount": group.refundAmount,
        "Created By": group.userID,
        "Created On": UtilMethods.formatDateTime(group.createdAt),
        "Updated By": group.updatedBy,
        "Updated At": UtilMethods.formatDateTime(group.updatedAt),
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, template);
    XLSX.writeFile(workbook, template + "_Registrations.xlsx");
  };

  const handleRetreatSelect = (event) => {
    setSelectedRetreat(event);
    setFilters({ ...filters, retreatID: event.id });
    setRetreatOptions(events);
  };

  const handleFilterSubmit = () => {
    fetchGroups();
  };

  const handleSearchRetreat = (e) => {
    setDebouncedSearch(e.target.value);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  if (loading) {
    return (
      <React.Fragment>
        <div className="container-fluid position-relative p-0">
          <MenuBar />
        </div>
        <LoadingPanel />
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <div className="container-fluid position-relative p-0">
          <MenuBar />
        </div>
        {/* Title Section */}
        <div className="container-fluid p-0">
          <div
            className="banner-image_sm"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
            }}
          >
            <div className="banner-heading text-center wow fadeInDown">
              <h1 className="text-white">Retreat Registration Reports</h1>
            </div>
          </div>
        </div>
        <div className="container-fluid mt-5 mb-5">
          <div>
            <div className="filter-panel d-flex align-items-left mb-4">
              <div className="col-md-2 col-12 mb-3 mb-md-0 me-2">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Registration ID"
                    name="registrationID"
                    value={filters.registrationID}
                    onChange={(e) =>
                      setFilters({ ...filters, registrationID: e.target.value })
                    }
                  />
                </div>
              </div>
              {/* Status Dropdown */}
              <div className="flex-grow-1 me-2">
                <div className="form-group mb-0">
                  <select
                    className="form-control"
                    name="status"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value })
                    }
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status.key} value={status.key}>
                        {status.value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-2 col-12 mb-3 mb-md-0 me-2">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Email"
                    name="email"
                    value={filters.email}
                    onChange={(e) =>
                      setFilters({ ...filters, email: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="col-md-2 col-12 mb-3 mb-md-0 me-2">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Phone"
                    name="phone"
                    value={filters.phone}
                    onChange={(e) =>
                      setFilters({ ...filters, phone: e.target.value })
                    }
                  />
                </div>
              </div>
              {/* Retreat Dropdown */}
              <div className="flex-grow-1 me-2">
                <div className="form-group mb-0 ">
                  <select
                    className="form-control"
                    name="retreatID"
                    value={filters.retreatID}
                    onChange={(e) =>
                      setFilters({ ...filters, retreatID: e.target.value })
                    }
                    required
                  >
                    <option value="">Select Retreat</option>
                    {events
                      .sort((a, b) => b.id - a.id)
                      .map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.id} : {event.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="col-md-2 col-12 d-flex justify-content-start mb-3 mb-md-0 me-2">
                <button
                  className="btn btn-primary btn-sm ml-2 me-2"
                  onClick={handleFilterSubmit}
                >
                  <FaList style={{ marginRight: "0.5rem" }} />
                  List
                </button>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-success btn-sm dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <FaFileExcel style={{ marginRight: "0.5rem" }} /> Excel
                  </button>

                  <ul className="dropdown-menu">
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => downloadExcel("Full Data")}
                      >
                        Full Data
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => downloadExcel("Participant")}
                      >
                        Participant
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => downloadExcel("Summary")}
                      >
                        Summary
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {groups.length === 0 ? (
              <p>No groups found.</p>
            ) : (
              <ul className="list-group mb-2">
                {groups.map((group) => (
                  <li key={group.registrationID}>
                    <strong>
                      Registration ID: {group.registrationID} {" |"}
                      {group.firstName} {group.lastName} {" |"}Email:{" "}
                      {group.email} {" |"}Phone: {group.phone} {" |"}Status:{" "}
                      {group.status} {" |"} Created By : {group.userID} {" |"}{" "}
                      Approved by : {group.updatedBy} on{" "}
                      {UtilMethods.formatDate(group.updatedAt)}
                    </strong>
                    <br />
                    <ul>
                      {group.groupMembers.map((member) => (
                        <li key={member.memberID}>
                          {member.firstName} {member.lastName} - Age:{" "}
                          {member.age}, Gender: {member.gender}, Relation:{" "}
                          {member.relation}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}

            {/* Error Modal */}
            <Modal show={showModal} onHide={handleCloseModal}>
              <Modal.Header closeButton>
                <Modal.Title>Error</Modal.Title>
              </Modal.Header>
              <Modal.Body>{error}</Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
        <FooterPanel />
      </React.Fragment>
    );
  }
};

export default GroupReport;
